import { useState } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, TrendingUp, Shield, Clock, Phone, Mail, Building2, ChevronRight, Star, ChevronDown, ClipboardList, CheckCircle2, AlertTriangle, Users, Award, Globe } from 'lucide-react'
import { Button } from '@/components/ui'
import { CITY_NAME } from '@/config/supabase'

const IMAGES = {
  lungomare: '/images/sanremo-lungomare.jpg',
  team: '/images/team-immo-sanremo.jpg',
  carlo: '/images/carlo-bessone.png',
  agenziaEsterno: '/images/agenzia-esterno.jpg',
  banner: '/images/vendere-casa-sanremo.jpg',
  properties: [
    '/images/casa-sanremo-01.jpg',
    '/images/casa-sanremo-02.jpg',
    '/images/casa-sanremo-03.jpg',
    '/images/casa-sanremo-04.jpg',
    '/images/casa-sanremo-05.jpg',
  ],
}

const features = [
  {
    icon: MapPin,
    title: 'Dati Reali del Compravenduto',
    description: 'Quotazioni immobiliari basate sulle compravendite reali degli ultimi 12 mesi per tutte le zone di Sanremo',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    icon: TrendingUp,
    title: 'Analisi Dettagliata al Mq',
    description: 'Coefficienti specifici per stato, piano, vista mare, ascensore e ogni caratteristica del tuo immobile a Sanremo',
    color: 'from-emerald-500 to-teal-500',
  },
  {
    icon: Clock,
    title: 'Risultato Immediato Online',
    description: 'Scopri il valore della tua casa in 2 minuti senza appuntamento e senza impegno',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: Shield,
    title: 'Gratuito e Senza Impegno',
    description: 'Valutazione completamente gratuita. I tuoi dati sono protetti e trattati con riservatezza',
    color: 'from-violet-500 to-purple-500',
  },
]

const stats = [
  { value: '500+', label: 'Valutazioni effettuate' },
  { value: '98%', label: 'Clienti soddisfatti' },
  { value: '15+', label: 'Anni di esperienza' },
]

const steps = [
  {
    icon: MapPin,
    number: '1',
    title: 'Seleziona la zona',
    description: 'Scegli la zona di Sanremo dove si trova il tuo immobile',
  },
  {
    icon: ClipboardList,
    number: '2',
    title: 'Inserisci i dettagli',
    description: 'Metratura, piano, stato dell\'immobile, vista mare e altre caratteristiche',
  },
  {
    icon: TrendingUp,
    number: '3',
    title: 'Ricevi la valutazione',
    description: 'Ottieni subito la stima del valore con il prezzo al metro quadro della tua zona',
  },
]

const zones = [
  { name: 'Centro Storico (La Pigna)', type: 'Zona storica' },
  { name: 'Corso Imperatrice', type: 'Zona centrale prestigiosa' },
  { name: 'Lungomare', type: 'Zona fronte mare' },
  { name: 'Foce', type: 'Zona residenziale' },
  { name: 'San Martino', type: 'Zona residenziale collinare' },
  { name: 'Coldirodi', type: 'Zona collinare panoramica' },
  { name: 'Bussana', type: 'Zona periferica' },
  { name: 'Poggio', type: 'Zona collinare' },
  { name: 'San Romolo', type: 'Zona montana' },
  { name: 'Verezzo', type: 'Zona collinare' },
  { name: 'San Giacomo', type: 'Zona residenziale' },
  { name: 'Zona Porto', type: 'Zona portuale' },
  { name: 'Corso Matuzia', type: 'Zona commerciale' },
  { name: 'Solaro', type: 'Zona residenziale' },
]

const testimonials = [
  {
    text: 'Carlo è agente immobiliare serio e preparato. Si è occupato con successo di una trattativa complessa. Vivamente consigliato',
    name: 'Stefano Giubertoni',
    date: '2024',
    initials: 'SG',
    bgColor: 'bg-gray-400',
  },
  {
    text: 'Ottima esperienza. Mi ha permesso di vendere il mio alloggio in meno di una settimana al prezzo che volevo',
    name: 'Luca Tarantino',
    date: '2024',
    initials: 'LT',
    bgColor: 'bg-orange-500',
  },
  {
    text: 'Serietà ed efficienza. Appartamento venduto in meno di tre settimane. Consigliata.',
    name: 'Cristina Moraldo',
    date: '2023',
    initials: 'CM',
    bgColor: 'bg-purple-500',
  },
  {
    text: 'Mi sono rivolta a Carlo per avere alcuni consigli su tematiche immobiliari, è stato molto gentile e professionale.',
    name: 'Maria Antonietta Capodanno',
    date: '2023',
    initials: 'MC',
    bgColor: 'bg-pink-500',
  },
  {
    text: "L'esperienza con Carlo e Curzia è stata decisamente positiva. Nonostante la situazione più complessa del normale, ne siamo usciti soddisfatti, in tempi brevi.",
    name: 'Barbara Albanese',
    date: '2023',
    initials: 'BA',
    bgColor: 'bg-blue-500',
  },
  {
    text: "Grazie all'agenzia ImmoSanremo sono riusciti a risolvere alcuni problemi.. grazie mille.. consigliatissimo",
    name: 'Ciccioskin88',
    date: '2024',
    initials: 'C',
    bgColor: 'bg-teal-500',
  },
]

const faqs = [
  {
    question: 'Come funziona la valutazione immobiliare online a Sanremo?',
    answer: 'Inserisci i dati del tuo immobile (zona, metratura, caratteristiche) nel nostro form e ricevi in 2 minuti una stima basata sui dati reali del compravenduto degli ultimi 12 mesi a Sanremo. Il nostro algoritmo applica coefficienti specifici per ogni caratteristica della tua proprietà: stato di conservazione, piano, vista mare, ascensore, terrazzo e box auto. Il risultato è una valutazione personalizzata con il prezzo al metro quadro aggiornato per la tua zona specifica di Sanremo.',
  },
  {
    question: 'La valutazione della casa a Sanremo è gratuita?',
    answer: 'Sì, la valutazione online è completamente gratuita e senza impegno. Non è richiesta alcuna registrazione né carta di credito. Riceverai una stima dettagliata del valore del tuo immobile basata sui dati reali delle compravendite immobiliari a Sanremo degli ultimi 12 mesi, con un report che include il range di prezzo, il prezzo al metro quadro e i fattori che influenzano la valutazione.',
  },
  {
    question: 'Quali dati vengono usati per la stima immobiliare a Sanremo?',
    answer: 'Utilizziamo i dati reali del compravenduto degli ultimi 12 mesi a Sanremo, ovvero i prezzi effettivi a cui gli immobili sono stati venduti nella tua zona. Questi dati vengono combinati con coefficienti specifici per stato dell\'immobile, piano, vista mare, presenza di ascensore, terrazzo, giardino e altre caratteristiche. Questo approccio basato su transazioni reali garantisce una stima più accurata rispetto a semplici medie di mercato.',
  },
  {
    question: 'Quanto è precisa la valutazione rispetto al valore reale?',
    answer: 'La nostra valutazione fornisce una stima indicativa molto attendibile perché basata su compravendite reali nella tua zona di Sanremo. Il margine di precisione dipende dalla quantità di transazioni recenti nella microzona specifica. Per immobili con caratteristiche particolari o per una perizia estimativa ufficiale da utilizzare in ambito legale o bancario, consigliamo di contattare la nostra agenzia ImmoSanremo per un sopralluogo professionale gratuito.',
  },
  {
    question: 'Quali zone di Sanremo sono coperte dalla valutazione?',
    answer: 'Copriamo tutte le zone di Sanremo: centro storico (La Pigna), Corso Imperatrice, Lungomare, Foce, San Martino, Coldirodi, Bussana, Poggio, San Romolo, Verezzo, San Giacomo, Zona Porto, Corso Matuzia e Solaro. Ogni zona ha quotazioni specifiche basate sul compravenduto reale, dalla costa alle colline, dalle zone centrali a quelle periferiche. Il nostro database copre tutte le microzone con dati aggiornati sulle transazioni immobiliari effettive.',
  },
  {
    question: 'Quanto vale una casa al metro quadro a Sanremo?',
    answer: 'Il prezzo al metro quadro a Sanremo varia significativamente per zona: le aree centrali e sul lungomare come Corso Imperatrice possono raggiungere i 4.000-5.000 €/mq per immobili ristrutturati con vista mare. Le zone residenziali come Foce e San Giacomo si attestano tra 2.500-3.500 €/mq, mentre le zone collinari come Coldirodi, Poggio e Verezzo partono da circa 1.500-2.500 €/mq. La nostra valutazione calcola il prezzo specifico per la tua zona e le caratteristiche del tuo immobile, basandosi sulle compravendite effettive degli ultimi mesi.',
  },
  {
    question: 'Serve una valutazione per richiedere un mutuo a Sanremo?',
    answer: 'Per la richiesta di un mutuo ipotecario, la banca invia un proprio perito per effettuare una perizia ufficiale dell\'immobile. Tuttavia, la nostra valutazione online gratuita è un ottimo strumento per avere un primo riferimento sul valore del tuo immobile prima di rivolgerti alla banca. Questo ti permette di stimare in anticipo l\'importo finanziabile e di confrontare le proposte di diversi istituti di credito. Tieni presente che la perizia bancaria tende ad essere più conservativa rispetto al prezzo di mercato, poiché la banca valuta l\'immobile come garanzia del credito.',
  },
  {
    question: 'Conviene ristrutturare casa prima di vendere a Sanremo?',
    answer: 'Dipende dal tipo di intervento e dalla zona. A Sanremo, un immobile ristrutturato può valere dal 10% al 20% in più rispetto a uno da ristrutturare nella stessa zona. Interventi mirati come il rifacimento del bagno, la tinteggiatura e l\'ammodernamento degli impianti possono aumentare significativamente l\'attrattività dell\'immobile e ridurre i tempi di vendita. Tuttavia, ristrutturazioni importanti potrebbero non essere convenienti se il costo supera il margine di rivalutazione. Il nostro consiglio è di effettuare prima una valutazione dell\'immobile nello stato attuale e poi valutare con l\'agenzia ImmoSanremo se e quali interventi conviene fare.',
  },
  {
    question: 'Quali tasse si pagano sulla compravendita immobiliare a Sanremo?',
    answer: 'Le tasse sulla compravendita variano in base al tipo di acquisto. Per la prima casa acquistata da privato: imposta di registro al 2% del valore catastale, più imposte ipotecaria e catastale fisse (50€ + 50€). Per la seconda casa da privato: imposta di registro al 9%. Se si acquista da costruttore (impresa): IVA al 4% per la prima casa o al 10% per la seconda casa, più imposte fisse. A queste si aggiunge l\'IMU per le seconde case, calcolata sulla rendita catastale rivalutata con l\'aliquota stabilita dal Comune di Sanremo. Per gli affitti brevi, è possibile optare per la cedolare secca al 21% (o 26% dal 2024 per la seconda unità in poi).',
  },
  {
    question: 'Quanto costa un\'agenzia immobiliare a Sanremo?',
    answer: 'Le provvigioni di un\'agenzia immobiliare a Sanremo variano generalmente dal 2% al 4% del prezzo di vendita, sia per il venditore che per l\'acquirente. Il servizio include la valutazione dell\'immobile, la promozione sui principali portali, la gestione delle visite, la negoziazione del prezzo, l\'assistenza burocratica fino al rogito notarile e, nel caso di ImmoSanremo, anche la valutazione online gratuita basata sui dati reali del compravenduto. Affidarsi a un professionista locale con esperienza nel mercato di Sanremo può fare la differenza in termini di prezzo ottenuto e tempi di vendita, spesso ripagando ampiamente il costo della provvigione.',
  },
]

const seoSections = [
  {
    title: 'Valutazione Immobiliare a Sanremo',
    content: (
      <div className="space-y-5">
        <p>
          Stai pensando di <strong>vendere casa a {CITY_NAME}</strong> o semplicemente vuoi sapere <strong>quanto vale il tuo immobile</strong>? Conoscere il valore reale della propria abitazione è il primo passo fondamentale per qualsiasi decisione immobiliare. Che si tratti di una compravendita, di una successione, di una separazione o della richiesta di un mutuo, avere una <strong>stima immobiliare accurata a {CITY_NAME}</strong> ti permette di muoverti con sicurezza nel mercato.
        </p>
        <p>
          Il nostro strumento di <strong>valutazione casa a {CITY_NAME}</strong> ti offre una stima professionale e gratuita in soli 2 minuti. A differenza di altre soluzioni generiche, la nostra valutazione si basa sui <strong>dati reali delle compravendite effettuate negli ultimi 12 mesi</strong> nella tua zona specifica di {CITY_NAME}. Questo significa che il prezzo al metro quadro che ottieni riflette l'andamento reale del mercato immobiliare locale, non semplici medie statistiche nazionali.
        </p>
        <p>
          Ogni immobile è unico: per questo il nostro algoritmo tiene conto di tutti i fattori che influenzano il <strong>valore di una casa a {CITY_NAME}</strong> — dalla posizione esatta alla metratura, dallo stato di conservazione al piano, dalla presenza dell'ascensore alla <strong>vista mare</strong>, fino al terrazzo, al giardino e al box auto. Il risultato è una valutazione personalizzata e dettagliata che ti mostra il range di prezzo del tuo immobile con il <strong>prezzo al metro quadro</strong> aggiornato per la tua zona.
        </p>
      </div>
    ),
  },
  {
    title: 'Prezzi e Tendenze del Mercato',
    content: (
      <div className="space-y-5">
        <p>
          Il <strong>mercato immobiliare a {CITY_NAME}</strong> è tra i più dinamici della <strong>Riviera Ligure di Ponente</strong>. Grazie al clima mite tutto l'anno, alla vicinanza con la Costa Azzurra e alla qualità della vita, {CITY_NAME} attira acquirenti italiani e stranieri alla ricerca di appartamenti, ville e immobili di prestigio. Il <strong>prezzo delle case a {CITY_NAME}</strong> varia significativamente in base alla zona, alla vicinanza al mare e alle caratteristiche dell'immobile.
        </p>
        <p>
          Nelle zone centrali e sul <strong>lungomare di {CITY_NAME}</strong>, come Corso Imperatrice e la zona del porto, i prezzi al metro quadro possono raggiungere e superare i <strong>4.000-5.000 €/mq</strong> per immobili ristrutturati con vista mare. Le zone residenziali come <strong>Foce</strong>, <strong>San Giacomo</strong> e <strong>Solaro</strong> offrono prezzi più accessibili, generalmente compresi tra <strong>2.500 e 3.500 €/mq</strong>. Le aree collinari come <strong>San Martino</strong>, <strong>Coldirodi</strong>, <strong>Poggio</strong> e <strong>Verezzo</strong> presentano valori tra <strong>1.500 e 2.500 €/mq</strong>, con il vantaggio di panorami mozzafiato e maggiore tranquillità.
        </p>
        <p>
          I fattori che incidono maggiormente sul <strong>valore di un immobile a {CITY_NAME}</strong> sono la <strong>vista mare</strong> (che può aumentare il valore fino al 15-20%), lo stato di conservazione (un appartamento da ristrutturare può valere il 10-15% in meno), il piano dell'immobile e la presenza di ascensore, terrazzo, giardino o box auto. Anche la luminosità, l'esposizione e la vicinanza a servizi, spiagge e al centro cittadino influiscono significativamente sulla valutazione finale.
        </p>
        <p>
          Rispetto ad altre località della <strong>Riviera dei Fiori</strong> come Bordighera, Ospedaletti o Imperia, {CITY_NAME} mantiene un posizionamento di mercato medio-alto grazie alla sua offerta di servizi, infrastrutture e alla notorietà internazionale legata al Festival della Canzone Italiana. <strong>Comprare casa a {CITY_NAME}</strong> rappresenta un investimento solido, sia per chi cerca una residenza principale che per chi desidera una seconda casa sulla costa ligure.
        </p>
      </div>
    ),
  },
  {
    title: 'Come Valutare Correttamente una Casa',
    content: (
      <div className="space-y-5">
        <p>
          <strong>Valutare una casa a {CITY_NAME}</strong> richiede di considerare molteplici fattori. Il primo è la <strong>posizione</strong>: un appartamento sul lungomare avrà un valore molto diverso da uno in zona collinare. Il secondo fattore è la <strong>metratura commerciale</strong>, che include non solo la superficie calpestabile ma anche balconi, terrazzi e pertinenze calcolati con le opportune percentuali. Lo <strong>stato di conservazione</strong> è determinante: un immobile ristrutturato di recente può valere fino al 20% in più rispetto a uno da ristrutturare nella stessa zona.
        </p>
        <p>
          La nostra <strong>valutazione immobiliare online</strong> si distingue perché utilizza i <strong>dati reali delle compravendite degli ultimi 12 mesi</strong> a {CITY_NAME}, non stime teoriche. Questo approccio garantisce che il prezzo al metro quadro rifletta il valore effettivo a cui gli immobili vengono realmente comprati e venduti nella tua zona. È utile fare una valutazione quando stai pensando di <strong>vendere casa</strong>, vuoi <strong>comprare un immobile</strong> e verificare se il prezzo richiesto è congruo, devi gestire una <strong>successione</strong> o una <strong>separazione patrimoniale</strong>, oppure hai bisogno di un riferimento per la richiesta di un <strong>mutuo</strong>.
        </p>
        <p>
          Per una stima ancora più precisa, dopo aver ottenuto la valutazione online puoi contattare la nostra agenzia <strong>ImmoSanremo</strong> per un sopralluogo professionale gratuito. I nostri agenti immobiliari conoscono ogni angolo di {CITY_NAME} e possono fornirti una <strong>perizia estimativa dettagliata</strong> che tiene conto di ogni singola caratteristica del tuo immobile.
        </p>
      </div>
    ),
  },
  {
    title: 'Vendere Casa a Sanremo: Guida Completa',
    content: (
      <div className="space-y-5">
        <p>
          <strong>Vendere casa a {CITY_NAME}</strong> è una decisione importante che richiede preparazione e strategia. Il primo passo fondamentale è conoscere il <strong>valore reale del tuo immobile</strong>: fissare un prezzo troppo alto significa allungare i tempi di vendita e scoraggiare i potenziali acquirenti, mentre un prezzo troppo basso rischia di farti perdere migliaia di euro. La nostra <strong>valutazione gratuita online</strong> ti offre un punto di partenza affidabile basato sulle compravendite effettive nella tua zona di {CITY_NAME}.
        </p>
        <p>
          Prima di mettere in <strong>vendita il tuo appartamento a {CITY_NAME}</strong>, è importante raccogliere tutta la documentazione necessaria: visura catastale aggiornata, planimetria conforme allo stato di fatto, attestato di prestazione energetica (APE), certificato di agibilità e, se presente, la documentazione condominiale con le ultime delibere assembleari e il regolamento. Avere tutto in ordine fin dall'inizio accelera notevolmente i tempi della <strong>compravendita immobiliare</strong>.
        </p>
        <p>
          Il mercato immobiliare di {CITY_NAME} presenta caratteristiche uniche: la domanda è sostenuta sia da acquirenti italiani in cerca di una <strong>seconda casa al mare</strong>, sia da compratori stranieri — in particolare francesi, tedeschi e svizzeri — attratti dal clima mite e dalla vicinanza alla Costa Azzurra. Affidarsi a un'<strong>agenzia immobiliare locale come ImmoSanremo</strong> significa avere accesso a una rete di contatti qualificati e a una conoscenza approfondita del territorio che può fare la differenza tra una vendita rapida al giusto prezzo e mesi di attesa.
        </p>
        <p>
          I tempi medi di <strong>vendita di un immobile a {CITY_NAME}</strong> variano in base alla zona e alla tipologia: gli appartamenti nelle zone centrali e sul lungomare si vendono mediamente più velocemente rispetto alle zone collinari, ma ogni caso è unico. Un immobile ben presentato, con un prezzo allineato al mercato e una promozione efficace, può trovare un acquirente in poche settimane.
        </p>
      </div>
    ),
  },
  {
    title: 'Comprare Casa a Sanremo: Cosa Sapere',
    content: (
      <div className="space-y-5">
        <p>
          <strong>Comprare casa a {CITY_NAME}</strong> è un'opportunità unica per chi cerca una residenza in una delle località più affascinanti della <strong>Riviera Ligure di Ponente</strong>. {CITY_NAME} offre un clima eccezionalmente mite tutto l'anno — con oltre 300 giorni di sole — una posizione strategica a soli 40 minuti dalla Costa Azzurra e dal confine francese, e una qualità della vita invidiabile grazie ai servizi, alle spiagge, al porto turistico e alla vivace vita culturale legata al Festival della Canzone Italiana.
        </p>
        <p>
          Prima di <strong>acquistare un immobile a {CITY_NAME}</strong>, è essenziale verificare alcuni aspetti fondamentali: la conformità urbanistica e catastale dell'immobile, lo stato di conservazione reale (un sopralluogo approfondito può rivelare problemi nascosti come umidità, infiltrazioni o impianti obsoleti), le spese condominiali ordinarie e straordinarie, e la classe energetica. Per gli immobili in condominio, è importante esaminare i verbali delle ultime assemblee per scoprire eventuali lavori straordinari deliberati o in programma.
        </p>
        <p>
          Il <strong>prezzo degli appartamenti a {CITY_NAME}</strong> varia enormemente in base alla posizione: si va dai <strong>1.500 €/mq</strong> nelle zone collinari più periferiche come San Romolo e Verezzo, fino a oltre <strong>5.000 €/mq</strong> per immobili di pregio con <strong>vista mare</strong> sul Corso Imperatrice o sul Lungomare. Le zone intermedie come <strong>Foce</strong>, <strong>San Giacomo</strong> e <strong>Solaro</strong> offrono un buon compromesso tra accessibilità dei prezzi, servizi e qualità della vita, con valori compresi tra <strong>2.500 e 3.500 €/mq</strong>.
        </p>
        <p>
          Per chi <strong>compra la prima casa a {CITY_NAME}</strong>, sono disponibili agevolazioni fiscali significative: imposta di registro ridotta al 2% (invece del 9%) o IVA al 4% per acquisti da costruttore, oltre a possibili agevolazioni per giovani under 36. Utilizzare la nostra <strong>valutazione online</strong> prima di un acquisto ti permette di verificare se il prezzo richiesto dal venditore è allineato ai valori di mercato reali della zona.
        </p>
      </div>
    ),
  },
  {
    title: 'Seconda Casa a Sanremo: Investimento e Opportunità',
    content: (
      <div className="space-y-5">
        <p>
          {CITY_NAME} è una delle mete più richieste in Italia per l'acquisto di una <strong>seconda casa al mare</strong>. La combinazione di clima mite mediterraneo, posizione strategica tra la <strong>Riviera dei Fiori</strong> e la <strong>Costa Azzurra</strong>, infrastrutture di qualità e un mercato immobiliare in crescita rende {CITY_NAME} un'opzione eccellente sia per chi cerca una casa vacanze sia per chi punta a un <strong>investimento immobiliare</strong> con ottimo rendimento.
        </p>
        <p>
          Dal punto di vista dell'<strong>investimento</strong>, una <strong>seconda casa a {CITY_NAME}</strong> può generare reddito attraverso gli <strong>affitti brevi e vacanzieri</strong>. La stagione turistica a {CITY_NAME} è particolarmente lunga — da marzo a ottobre — grazie al clima favorevole, e si intensifica durante eventi come il Festival di Sanremo, il Rally di Sanremo e la Milano-Sanremo ciclistica. Un appartamento bilocale ristrutturato in zona centrale può generare un rendimento lordo annuo del <strong>4-6%</strong> attraverso locazioni brevi.
        </p>
        <p>
          Per quanto riguarda la <strong>tassazione della seconda casa</strong>, è importante considerare l'<strong>IMU</strong> (Imposta Municipale Unica), che per le seconde case a {CITY_NAME} si calcola con un'aliquota stabilita dal Comune sulla rendita catastale rivalutata. A questo si aggiunge la <strong>TARI</strong> (tassa rifiuti) e, in caso di locazione, la possibilità di optare per la <strong>cedolare secca</strong> al 21% (o 26% per affitti brevi dal 2024 per la seconda unità in poi). L'imposta di registro per l'acquisto della seconda casa è del <strong>9%</strong> del valore catastale, oppure IVA al 10% se si acquista da costruttore.
        </p>
        <p>
          Rispetto ad altre località della <strong>Riviera Ligure</strong>, {CITY_NAME} offre un rapporto qualità-prezzo competitivo. <strong>Bordighera</strong> e <strong>Ospedaletti</strong> presentano prezzi simili ma con meno servizi e infrastrutture. <strong>Imperia</strong> ha prezzi leggermente più bassi ma è meno rinomata a livello internazionale. <strong>Ventimiglia</strong>, al confine francese, attira chi lavora in Costa Azzurra ma ha un'offerta immobiliare più limitata. {CITY_NAME} resta la scelta ideale per chi cerca il giusto equilibrio tra prestigio, servizi e potenziale di rivalutazione.
        </p>
      </div>
    ),
  },
  {
    title: 'Perizia Immobiliare vs Valutazione Online',
    content: (
      <div className="space-y-5">
        <p>
          Quando si parla di <strong>valutazione immobiliare a {CITY_NAME}</strong>, è importante distinguere tra la <strong>valutazione online gratuita</strong> e la <strong>perizia immobiliare professionale</strong>. La nostra valutazione online utilizza i dati reali del compravenduto degli ultimi 12 mesi e applica coefficienti specifici per le caratteristiche del tuo immobile, fornendo una <strong>stima indicativa affidabile</strong> del valore di mercato. È uno strumento ideale per avere un primo riferimento rapido e gratuito.
        </p>
        <p>
          La <strong>perizia immobiliare</strong>, invece, è un documento redatto da un professionista abilitato (perito o tecnico iscritto all'albo) che effettua un sopralluogo fisico dell'immobile, verifica la conformità urbanistica e catastale, analizza lo stato manutentivo e produce una <strong>relazione estimativa dettagliata</strong>. Questo tipo di valutazione ha valore legale e viene richiesta in specifiche circostanze: per l'erogazione di un <strong>mutuo ipotecario</strong> (la banca invia il proprio perito), in caso di <strong>successione ereditaria</strong>, durante una <strong>separazione o divorzio</strong> per la divisione dei beni, e in <strong>contenziosi legali</strong> relativi a immobili.
        </p>
        <p>
          Il nostro consiglio è di utilizzare la <strong>valutazione online gratuita</strong> come primo step per orientarti sul valore del tuo immobile a {CITY_NAME}. Se poi hai bisogno di una <strong>perizia ufficiale</strong>, l'agenzia <strong>ImmoSanremo</strong> può metterti in contatto con professionisti qualificati del territorio. In molti casi, i nostri clienti utilizzano la valutazione online per decidere se procedere con la vendita e poi richiedono un sopralluogo gratuito ai nostri agenti per una stima ancora più precisa.
        </p>
        <p>
          È utile sapere che la <strong>stima di un perito bancario</strong> tende ad essere più conservativa rispetto al prezzo di mercato, poiché le banche valutano l'immobile in un'ottica di garanzia del credito. La nostra valutazione, basata sulle <strong>compravendite effettive</strong>, riflette invece il prezzo reale a cui gli immobili vengono venduti nella tua zona, offrendo un quadro più completo del <strong>mercato immobiliare locale di {CITY_NAME}</strong>.
        </p>
      </div>
    ),
  },
]

export function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [showAllReviews, setShowAllReviews] = useState(false)
  const [openSeoSection, setOpenSeoSection] = useState<number | null>(null)

  const visibleTestimonials = showAllReviews ? testimonials : testimonials.slice(0, 3)

  return (
    <div className="flex-1 overflow-hidden">
      {/* ===== SEZIONE 1: HERO (Conversion-focused) ===== */}
      <section className="relative text-white min-h-[90vh] flex items-center">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${IMAGES.lungomare})` }}
        />
        {/* Dark overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/90 via-primary-800/85 to-primary-900/90" />
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl animate-float" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-primary-400/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
          <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-cyan-400/10 rounded-full blur-2xl animate-float" style={{ animationDelay: '1.5s' }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full text-center">
          {/* Badge */}
          <div className="animate-on-load animate-fade-in-up inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-8">
            <Shield className="h-4 w-4 text-accent-400" />
            <span className="text-sm font-medium text-primary-100">100% Gratuito &middot; Senza Impegno</span>
          </div>

          <h1 className="animate-on-load animate-fade-in-up text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
            Ricevi la Valutazione della Tua
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-300 to-accent-400 mt-2">
              Casa in 2 Minuti
            </span>
          </h1>

          <p className="animate-on-load animate-fade-in-up-delay-1 mt-6 text-lg md:text-xl text-primary-100/90 max-w-2xl mx-auto leading-relaxed">
            Scopri <strong>gratuitamente</strong> quanto vale il tuo immobile a {CITY_NAME}.
            Basata sui dati reali delle compravendite degli ultimi 12 mesi.
          </p>

          <div className="animate-on-load animate-fade-in-up-delay-2 mt-10">
            <Link to="/valutazione">
              <button className="pulse-cta bg-accent-400 hover:bg-accent-500 text-gray-900 font-bold px-12 py-5 text-xl md:text-2xl rounded-2xl shadow-xl hover:shadow-2xl transition-all shimmer-btn inline-flex items-center gap-2">
                SCOPRI IL VALORE ORA
                <ChevronRight className="h-6 w-6" />
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="animate-on-load animate-fade-in-up-delay-3 mt-12 flex gap-8 justify-center">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-xs md:text-sm text-primary-200 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 2: TRUST BAR ===== */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="flex -space-x-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <span className="font-semibold">5.0 Google Reviews</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <TrendingUp className="h-4 w-4 text-primary-500" />
              <span>500+ Valutazioni</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Shield className="h-4 w-4 text-primary-500" />
              <span>Dati Compravenduto Reale</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-4 w-4 text-primary-500" />
              <span>Risultato in 2 Min</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 3: URGENZA MERCATO ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-6">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-medium text-amber-700">Mercato in evoluzione</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Cosa Sta Succedendo ai Prezzi delle Case a {CITY_NAME}?
              </h2>

              <div className="text-gray-600 text-lg leading-relaxed space-y-5">
                <p>
                  Il mercato immobiliare a {CITY_NAME} sta cambiando rapidamente. I prezzi variano di zona in zona e mese dopo mese. <strong>Sapere quanto vale realmente la tua casa oggi</strong> pu&ograve; fare la differenza di migliaia di euro.
                </p>
                <p>
                  Non affidarti a stime generiche o a portali che usano medie nazionali. La nostra valutazione si basa sulle <strong>compravendite effettive degli ultimi 12 mesi</strong> nella tua zona specifica di {CITY_NAME}.
                </p>
              </div>

              <div className="mt-10">
                <Link to="/valutazione">
                  <Button size="lg" className="px-8 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all rounded-xl">
                    Scopri il Valore Reale
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="hidden lg:block">
              <img
                src={IMAGES.agenziaEsterno}
                alt="Agenzia ImmoSanremo"
                className="w-full rounded-2xl shadow-xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 4: COME FUNZIONA ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Semplice e veloce</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Come Funziona la Valutazione Immobiliare a {CITY_NAME}
            </h2>
            <p className="mt-4 text-gray-500 max-w-2xl mx-auto">
              Tre semplici passaggi per scoprire il valore della tua casa a {CITY_NAME} basato su dati reali del compravenduto.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connecting line on desktop */}
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-primary-200 via-primary-400 to-primary-200" />

            {steps.map((step, index) => (
              <div key={step.number} className="relative text-center" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="relative inline-flex items-center justify-center w-32 h-32 mb-6">
                  <div className="absolute inset-0 rounded-full bg-primary-50" />
                  <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
                    <step.icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center shadow">
                    {step.number}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/valutazione">
              <button className="bg-accent-400 hover:bg-accent-500 text-gray-900 font-bold px-10 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2">
                INIZIAMO
                <ChevronRight className="h-5 w-5" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 5: TESTIMONIANZE ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Recensioni</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Cosa Dicono i Nostri Clienti
            </h2>
            {/* Google aggregate badge */}
            <div className="mt-4 inline-flex items-center gap-2 bg-gray-50 rounded-full px-5 py-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <div className="flex -space-x-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <span className="text-sm font-semibold text-gray-700">5.0 su Google</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleTestimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <svg className="h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${testimonial.bgColor} flex items-center justify-center`}>
                    <span className="text-white font-semibold text-sm">
                      {testimonial.initials}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-xs text-gray-400">Google Review &middot; {testimonial.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!showAllReviews && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAllReviews(true)}
                className="text-primary-600 hover:text-primary-700 font-semibold text-sm inline-flex items-center gap-1 transition-colors"
              >
                Vedi tutte le recensioni
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/valutazione" className="text-primary-600 hover:text-primary-700 font-semibold text-sm inline-flex items-center gap-1 transition-colors">
              Anche tu puoi scoprire il valore della tua casa
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 6: CHI SIAMO ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">La nostra agenzia</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Chi &egrave; ImmoSanremo?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={IMAGES.team}
                alt="Il team di ImmoSanremo - Carlo e Curzia"
                className="w-full rounded-2xl shadow-lg mb-8 object-cover"
              />
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                <strong>ImmoSanremo</strong> &egrave; un'agenzia immobiliare con sede a Sanremo, nella Riviera dei Fiori.
                Con <strong>oltre 15 anni di esperienza</strong> nel mercato immobiliare locale, Carlo, Curzia e il team di ImmoSanremo
                conoscono ogni angolo della citt&agrave; e ogni sfumatura del mercato.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                La nostra missione &egrave; offrire <strong>valutazioni trasparenti e accurate</strong>, basate su dati reali
                del compravenduto, per aiutarti a prendere le migliori decisioni immobiliari. Centinaia di clienti
                si sono gi&agrave; affidati a noi con successo.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
                    <Award className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">15+ Anni</p>
                    <p className="text-xs text-gray-500">di esperienza</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">500+</p>
                    <p className="text-xs text-gray-500">clienti serviti</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Sanremo</p>
                    <p className="text-xs text-gray-500">esperti locali</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact card */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-gray-900">ImmoSanremo</h3>
                  <p className="text-sm text-gray-500">Professionisti dal 2009</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600">
                    Corso Matuzia, 22<br />
                    Sanremo (Imperia) - 18038
                  </p>
                </div>

                <a href="tel:+390184610689" className="flex items-center gap-3 text-sm text-gray-600 hover:text-primary-600 transition-colors group">
                  <Phone className="h-5 w-5 text-primary-500" />
                  +39 0184 610689
                </a>

                <a href="mailto:info@immosanremo.com" className="flex items-center gap-3 text-sm text-gray-600 hover:text-primary-600 transition-colors group">
                  <Mail className="h-5 w-5 text-primary-500" />
                  info@immosanremo.com
                </a>

                <a href="https://www.immosanremo.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-gray-600 hover:text-primary-600 transition-colors group">
                  <Globe className="h-5 w-5 text-primary-500" />
                  www.immosanremo.com
                </a>
              </div>

              {/* Trust badge */}
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-2">
                <div className="flex -space-x-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <span className="text-xs text-gray-500 ml-1">5.0 su Google &middot; Recensioni verificate</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 7: VANTAGGI ===== */}
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">I nostri vantaggi</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Perch&eacute; Scegliere la Nostra Valutazione Immobiliare
            </h2>
            <p className="mt-4 text-gray-500 max-w-2xl mx-auto">
              Tecnologia avanzata e dati reali del compravenduto per una valutazione precisa e affidabile del tuo immobile a {CITY_NAME}.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="feature-card bg-gray-50 rounded-2xl p-6 border border-gray-100"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 shadow-lg`}>
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEZIONE SEO: MERCATO IMMOBILIARE 2026 ===== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Mercato Immobiliare {CITY_NAME} 2026: Prezzi e Previsioni
          </h2>
          <div className="prose prose-lg max-w-none text-gray-600 space-y-5 leading-relaxed">
            <p>
              Il <strong>mercato immobiliare a {CITY_NAME} nel 2026</strong> si conferma dinamico e in leggera crescita, sostenuto dalla domanda costante di acquirenti italiani e internazionali. Dopo un biennio di consolidamento, i <strong>prezzi delle case a {CITY_NAME}</strong> mostrano un trend positivo soprattutto nelle zone centrali e fronte mare, dove la disponibilità di immobili di qualità è limitata e la domanda supera l'offerta. Le zone collinari registrano un interesse crescente, in particolare da parte di chi cerca spazi più ampi e tranquillità senza rinunciare alla vicinanza al mare.
            </p>
            <p>
              Tra i fattori che influenzano il <strong>mercato immobiliare sanremese nel 2026</strong>, spiccano l'aumento dell'attrattività turistica della Riviera dei Fiori, i miglioramenti infrastrutturali in corso (inclusa la nuova pista ciclabile costiera e il potenziamento dei collegamenti ferroviari), e la crescente popolarità degli <strong>affitti brevi</strong> che rendono l'investimento immobiliare a {CITY_NAME} particolarmente interessante per chi cerca una rendita. La vicinanza alla <strong>Costa Azzurra</strong> e al <strong>Principato di Monaco</strong> continua ad attrarre una clientela internazionale con alto potere d'acquisto.
            </p>
            <p>
              Per chi sta valutando di <strong>comprare o vendere casa a {CITY_NAME}</strong> nel 2026, è fondamentale avere una <strong>valutazione aggiornata</strong> basata sulle transazioni più recenti. Il nostro strumento utilizza i dati del compravenduto degli ultimi 12 mesi, garantendo una stima che riflette le condizioni attuali del mercato e non dati storici ormai superati. Che tu stia pensando di vendere il tuo appartamento, acquistare una seconda casa o semplicemente monitorare il valore del tuo patrimonio immobiliare, una valutazione periodica è il modo migliore per restare informato.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 8: CONTENUTI SEO (Accordion) ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Approfondimenti</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Tutto sul Mercato Immobiliare a {CITY_NAME}
            </h2>
          </div>

          <div className="space-y-4">
            {seoSections.map((section, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenSeoSection(openSeoSection === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">{section.title}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                      openSeoSection === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={openSeoSection === index ? 'block' : 'sr-only'}>
                  <div className="px-5 pb-5 prose prose-lg max-w-none text-gray-600 leading-relaxed">
                    {section.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 9: ZONE DI SANREMO ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Copertura completa</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Valutazioni Immobiliari in Tutte le Zone di {CITY_NAME}
            </h2>
            <p className="mt-4 text-gray-500 max-w-2xl mx-auto">
              Il nostro strumento copre tutte le zone di {CITY_NAME}, dalla costa alle colline.
              Ogni zona ha quotazioni specifiche basate sul compravenduto reale per una stima il pi&ugrave; accurata possibile.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {zones.map((zone) => (
              <div
                key={zone.name}
                className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-primary-200 hover:bg-primary-50/50 transition-all group"
              >
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-primary-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-primary-700 transition-colors">{zone.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{zone.type}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 max-w-3xl mx-auto text-gray-500 text-sm leading-relaxed space-y-3">
            <p>
              Ogni zona di {CITY_NAME} ha le sue peculiarità che influenzano il <strong>prezzo degli immobili</strong>. Il <strong>centro storico della Pigna</strong> offre appartamenti caratteristici a prezzi contenuti, ideali per chi ama l'atmosfera medievale. <strong>Corso Imperatrice</strong> e il <strong>Lungomare</strong> sono le zone più prestigiose, con immobili di alto standing e vista mare. Le zone residenziali di <strong>Foce</strong> e <strong>San Giacomo</strong> sono molto richieste dalle famiglie per la vicinanza a scuole e servizi. Le frazioni collinari come <strong>Coldirodi</strong>, <strong>Poggio</strong> e <strong>Verezzo</strong> attraggono chi cerca tranquillità e spazi verdi, con ottimo rapporto qualità-prezzo. <strong>Bussana</strong>, sia vecchia che nuova, offre soluzioni interessanti per chi desidera vivere in un contesto più raccolto senza rinunciare alla vicinanza al mare.
            </p>
            <p>
              La <strong>zona Porto</strong> è particolarmente apprezzata dagli appassionati di nautica e da chi cerca la vivacità del waterfront, mentre <strong>Corso Matuzia</strong> rappresenta il cuore commerciale della città con ottima accessibilità e servizi. <strong>San Martino</strong> e <strong>Solaro</strong> sono quartieri residenziali tranquilli, ben collegati al centro, perfetti per famiglie e professionisti. <strong>San Romolo</strong>, in collina, offre panorami spettacolari e prezzi tra i più accessibili di {CITY_NAME}, ideale per chi ama la natura e non teme la distanza dal mare.
            </p>
          </div>

          {/* Gallery proprietà */}
          <div className="mt-12">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Immobili a Sanremo</h3>
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory">
              {IMAGES.properties.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={`Immobile in vendita a Sanremo ${index + 1}`}
                  className="w-48 h-48 object-cover rounded-xl shadow-md flex-shrink-0 snap-center"
                />
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/valutazione">
              <Button variant="outline" size="lg" className="px-8 py-3 text-base font-semibold rounded-xl">
                Trova la Tua Zona e Scopri il Valore
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 10: FAQ ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Domande Frequenti sulla Valutazione Casa a {CITY_NAME}
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-base font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 11: CTA FINALE ===== */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-800" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <p className="text-primary-200 text-sm font-medium mb-4 uppercase tracking-wider">
            Non aspettare che i prezzi cambino
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Scopri Subito Quanto Vale la Tua Casa a {CITY_NAME}
          </h2>
          <p className="text-xl text-primary-100 mb-10 max-w-2xl mx-auto">
            Compila il form in 2 minuti e ricevi la valutazione immobiliare gratuita
            basata sui dati reali del compravenduto degli ultimi 12 mesi.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/valutazione">
              <button className="pulse-cta bg-accent-400 hover:bg-accent-500 text-gray-900 font-bold px-12 py-5 text-xl rounded-2xl shadow-xl hover:shadow-2xl transition-all shimmer-btn inline-flex items-center gap-2">
                SCOPRI IL VALORE ORA
                <ChevronRight className="h-6 w-6" />
              </button>
            </Link>
          </div>
          <p className="mt-6 text-primary-200 text-sm">
            Oppure chiama il{' '}
            <a href="tel:+390184610689" className="text-white font-semibold hover:underline">
              +39 0184 610689
            </a>
            {' '}per una consulenza personalizzata
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-6 text-primary-200 text-xs">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              100% Gratuito
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              Dati Reali Compravenduto
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              Risultato in 2 Minuti
            </span>
          </div>
        </div>
      </section>
    </div>
  )
}
